import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography } from '@mui/material'
import axios from 'axios'
import jwt_decode from 'jwt-decode'
import moment from 'moment'
import { useEffect, useState } from 'react'
import Select from 'react-select'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import unifiedSocketClient from '../../utils/unifiedSocketClient'
import { TIME_TRACKING_STATUS } from '../../utils/timeTrackingStatuses'
import { TICKETING_API } from '../../utils/consts'
function TimeTrackerWidget() {
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [startTime, setStartTime] = useState(null)
  const [openModal, setOpenModal] = useState(false)
  const [pendingStatus, setPendingStatus] = useState(null)
  const [isUpdating, setIsUpdating] = useState(false)

  const statusOptions = Object.values(TIME_TRACKING_STATUS).map((status) => ({
    value: status,
    label: status
  }))

  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      boxShadow: 'none',
      backgroundColor: '#f5f5f5',
      minHeight: '56px',
      cursor: 'pointer',
      '&:hover': {
        border: '1px solid #aaa'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#5c6ac4' : state.isFocused ? '#f0f2ff' : null,
      color: state.isSelected ? 'white' : '#333',
      padding: '10px 15px',
      cursor: 'pointer'
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      width: '400px',
      zIndex: 9999
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#666'
    }),
    singleValue: (provided) => ({
      ...provided,
      padding: '5px 0'
    })
  }

  const getMyTimeTracking = async () => {
    try {
      const myResponse = await axios.get(`${TICKETING_API}/time-tracking/today`)
      if (myResponse.data.data?.statuses?.length > 0) {
        const myTimeTracking = myResponse.data.data.statuses[0]
        setSelectedStatus({ value: myTimeTracking.status, label: myTimeTracking.status })
        setStartTime(myTimeTracking.timestamp)
      } else {
        setSelectedStatus(null)
        setStartTime(null)
      }
    } catch (error) {
      console.error('Error fetching time tracking:', error)
    }
  }

  // Initial fetch of time tracking data
  useEffect(() => {
    getMyTimeTracking()
  }, [])

  // Set up unified socket message listeners for real-time updates
  // Note: Unified socket connection is already established in Dashboard.js
  useEffect(() => {
    // Listen to unified socket events for time tracking updates
    const cleanupUnifiedSocket = unifiedSocketClient.on('timeTrackingStatusUpdated', (data) => {
      console.log('📨 TimeTrackerWidget: Time tracking status updated:', data)

      // Check if this update is for the current user
      const localStorageToken = localStorage.getItem('token')
      const decodedToken = jwt_decode(localStorageToken)
      const timeData = data.data || data

      if (timeData.userId === decodedToken.user_id) {
        console.log('🕐 TimeTrackerWidget: Time tracking update for current user, updating widget...')

        // Update widget state directly with the last status from the event
        if (timeData.statuses && timeData.statuses.length > 0) {
          const lastStatus = timeData.statuses[timeData.statuses.length - 1]

          // Update selectedStatus
          setSelectedStatus({
            value: lastStatus.status,
            label: lastStatus.status
          })

          // Update startTime
          setStartTime(lastStatus.timestamp)

          console.log('✅ Widget updated - Status:', lastStatus.status, 'Time:', lastStatus.timestamp)
        }
      }
    })

    // Also listen to notification events for time tracking updates
    const cleanupNotificationSocket = unifiedSocketClient.on('notification', (data) => {
      try {
        const notificationData = data.data || data
        console.log('📨 TimeTrackerWidget: Notification received:', notificationData)

        // Check if it's a time tracking update notification
        if (
          notificationData.type === 'time_tracking_update' ||
          notificationData.category === 'time_tracking' ||
          (notificationData.title && notificationData.title.toLowerCase().includes('time tracking'))
        ) {
          console.log('🕐 TimeTrackerWidget: Time tracking update detected, refreshing data...')
          getMyTimeTracking()
        }
      } catch (error) {
        console.error('Error parsing notification message:', error)
      }
    })

    // Cleanup on unmount - remove both listeners
    return () => {
      cleanupUnifiedSocket()
      cleanupNotificationSocket()
    }
  }, [])

  const handleChange = async (selectedOption) => {
    // Prevent multiple simultaneous calls
    if (isUpdating) {
      return
    }

    // If Clock Out is selected, show confirmation modal
    if (selectedOption.value === TIME_TRACKING_STATUS.CLOCK_OUT) {
      setPendingStatus(selectedOption)
      setOpenModal(true)
      return
    }

    // Otherwise update status immediately
    await updateStatus(selectedOption)
  }

  const updateStatus = async (selectedOption) => {
    try {
      setIsUpdating(true)
      
      const myResponse = await axios.post(`${TICKETING_API}/time-tracking/update-status`, {
        status: selectedOption.value
      })

      toast.success(`Status updated to "${selectedOption.label}"`)

      if (myResponse.data.data?.statuses?.length > 0) {
        const myTimeTracking = myResponse.data.data.statuses[myResponse.data.data.statuses.length - 1]
        setSelectedStatus({ value: myTimeTracking.status, label: myTimeTracking.status })
        setStartTime(myTimeTracking.timestamp)
      } else {
        setSelectedStatus(null)
        setStartTime(null)
      }
    } catch (error) {
      console.error('Error updating time tracking status:', error)
      toast.error('Failed to update status. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setPendingStatus(null)
  }

  const handleConfirmClockOut = async () => {
    if (pendingStatus && !isUpdating) {
      await updateStatus(pendingStatus)
    }
    handleCloseModal()
  }

  const formatValueContainer = ({ value }) => {
    if (isUpdating) {
      return (
        <Box display='flex' flexDirection='column'>
          <Typography>Updating...</Typography>
          <Typography variant='caption' style={{ color: '#5c6ac4' }}>
            Please wait
          </Typography>
        </Box>
      )
    }

    if (!value || !startTime) return 'Select Status'

    return (
      <Box display='flex' flexDirection='column'>
        <Typography>{value.label}</Typography>
        <Typography variant='caption' style={{ color: '#5c6ac4' }}>
          Started: {moment(startTime).format('HH:mm')}
        </Typography>
      </Box>
    )
  }

  return (
    <div style={{ minWidth: '200px' }}>
      <Select
        styles={customStyles}
        options={statusOptions}
        value={selectedStatus}
        onChange={handleChange}
        placeholder='Select Status'
        components={{
          SingleValue: () => formatValueContainer({ value: selectedStatus })
        }}
        isSearchable
        menuPlacement='auto'
        isOptionDisabled={(option) => option.value === selectedStatus?.value}
        isDisabled={isUpdating}
      />

      {/* Confirmation Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} aria-labelledby='clock-out-confirmation-dialog'>
        <DialogTitle>Confirm Clock Out</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clock out? This will be considered as the end of your workday and you won't be able to check in again
            today.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color='primary' disabled={isUpdating}>
            Cancel
          </Button>
          <Button onClick={handleConfirmClockOut} color='primary' variant='contained' autoFocus disabled={isUpdating}>
            {isUpdating ? 'Updating...' : 'Confirm Clock Out'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default TimeTrackerWidget
